/**
 * Admin Panel - Reports & Economy Screen
 *
 * Two tabs:
 * - Usage: cost-per-minute analytics, token breakdowns, pipeline step costs
 * - Financial: revenue vs provider cost, margin analysis, per-org P&L
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, Globe } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { KpiCard } from '@/components/dashboard/kpi-card';
import { ExportButton } from '@/components/charts/ExportButton';
import { CHART_COLORS } from '@/components/data';
import {
  InteractiveChart,
  type ChartDataPoint,
} from '@/components/charts/InteractiveChart';
import { useRole } from '@/contexts/RoleContext';
import { useApiClient } from '@/hooks/useApiClient';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { API_BASE_URL } from '@/lib/api/config';
import { useToast } from '@/contexts/ToastContext';
import { logError } from '@/lib/logger';
import { cn, formatNumber, resolveLocale } from '@/lib/utils';
import {
  formatCost,
  formatCostFromCents,
  formatDollars,
  formatRateAmount,
} from '@/lib/currency';
import { billingSummaryQueryKey, billingMarginQueryKey } from '@/lib/query-options';
import type {
  BillingSummaryResponse,
  MarginReportApiResponse,
} from '@/lib/api/types/billing.types';
import { AdminLayout } from './components/AdminLayout';
import { TableSkeleton } from '@/components/ui/skeletons';
import { PeriodBar, type Period } from '@/components/layout/PeriodBar';
import { DEFAULT_WEEK_STARTS_ON } from '@aica/core';
import type { OrgFinancialReport, OrgFinancialReportRow } from '@aica/api-types';
import { fetchOrgFinancialReport } from '@/lib/api/admin-financial-report';
import { usePeriodNavigation } from '@/hooks/usePeriodNavigation';
import { CONTROL_MIN_HEIGHT, CONTROL_SQUARE } from '@/components/ui/control-size';

// ============================================================================
// Route
// ============================================================================

export const Route = createFileRoute('/admin/reports')({
  component: AdminReportsPage,
});

// ============================================================================
// Types
// ============================================================================

type ReportTab = 'usage' | 'financial' | 'organizations';
type PeriodType = 'day' | 'week' | 'month';

// -- Usage API types ----------------------------------------------------------

interface UsageStep {
  costType: string;
  provider: string;
  entries: number;
  totalCostCents: number;
  totalMinutes: number;
  totalTokensIn: number;
  totalTokensOut: number;
  entriesWithTokens: number;
}

interface UsageReportData {
  periodStart: string;
  periodEnd: string;
  totalCostCents: number;
  totalMinutes: number;
  totalTokensIn: number;
  totalTokensOut: number;
  totalEntries: number;
  steps: UsageStep[];
}

interface UsageTrendPoint {
  bucket: string;
  costType: string;
  provider: string;
  totalCostCents: number;
  totalTokensIn: number;
  totalTokensOut: number;
  entries: number;
}

// -- Legacy financial types ---------------------------------------------------

interface MonthlyTrend {
  month: string;
  /** Revenue (usage + subscription), dollars. */
  revenue: number;
  providerCost: number;
}

interface FinancialReportData {
  summary: BillingSummaryResponse | null;
  margin: MarginReportApiResponse['margin'] | null;
  periodStart: string;
  periodEnd: string;
}

// -- Sort state ---------------------------------------------------------------

type OrgSortKey = keyof OrgFinancialReportRow;
type SortDir = 'asc' | 'desc';

// ============================================================================
// Period helpers
// ============================================================================

function granularityForPeriod(period: PeriodType): 'hour' | 'day' {
  return period === 'day' ? 'hour' : 'day';
}

const REPORT_PERIODS: { id: Period; label: string }[] = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
];

// ============================================================================
// Step color mapping
// ============================================================================

// Четыре литерала ниже старше этой правки; новая строка берёт цвет из палитры
// Meridian (`CHART_COLORS`), а не добавляет пятый. Совпадение не случайно:
// `#4db8b3` — это `CHART_COLORS.teal`, так что перевод остальных на токены —
// механическая замена, но она про соседний код, а не про этот разбор.
const STEP_COLORS: Record<string, string> = {
  transcription: '#4db8b3',
  analysis: '#8b5cf6',
  script_adherence: '#f59e0b',
  'usage_debit.categorization': '#ef4444',
  categorization: '#ef4444',
  script_parse: CHART_COLORS.purple,
  assistant: CHART_COLORS.sky,
  criteria: CHART_COLORS.emerald,
  // Не `amber`: `#f6b352` и уже занятый `script_adherence` (`#f59e0b`) — один и
  // тот же оттенок на глаз, а в столбце расходов они стоят рядом, и «сколько
  // стоили проверки формулировки» читалось бы как часть соответствия скрипту
  // (`/code-review`). `lime` держит родство с зелёным `criteria` и при этом
  // различим.
  criteria_trial: CHART_COLORS.lime,
  // Помощник «своими словами» и правка по примерам (#1416): тоже траты на
  // НАСТРОЙКУ критерия, того же рода, что проверка; свободного цвета в палитре
  // нет (`teal` — у транскрипции, `amber` — у соответствия скрипту), поэтому
  // оттенок общий с проверкой, а различает подпись.
  criteria_draft: CHART_COLORS.lime,
  criteria_revise: CHART_COLORS.lime,
  embedding: CHART_COLORS.pink,
  voice_extraction: CHART_COLORS.cyan,
  deal_state: CHART_COLORS.gold,
  // Черновик сообщения клиенту (#1675): трата на сделку, того же рода, что
  // «состояние сделки»; свободного цвета в палитре нет, оттенок общий, различает
  // подпись.
  deal_client_message: CHART_COLORS.gold,
  voice_faq_draft: CHART_COLORS.orange,
  stage_phase_classify: CHART_COLORS.rose,
  theme_bootstrap: CHART_COLORS.coral,
  // Недельная сводка новых тем (#845, PR 5b): тот же оттенок, что у стартового
  // предложения, намеренно — обе траты на набор тем, а свободного цвета в
  // палитре нет (`amber` занят по смыслу, см. выше); различает подпись.
  theme_proposals: CHART_COLORS.coral,
};

function stepColor(costType: string): string {
  return STEP_COLORS[costType] ?? '#6b7280';
}

function stepLabel(costType: string, t: TFunction): string {
  const keyByCostType: Record<string, string> = {
    transcription: 'stepNames.transcription',
    analysis: 'stepNames.analysis',
    script_adherence: 'stepNames.scriptAdherence',
    'usage_debit.categorization': 'stepNames.categorization',
    categorization: 'stepNames.categorization',
    // Настроечный разбор скрипта: наш расход, клиенту не выставляется (#509).
    // Без подписи строка отчёта показала бы сырой идентификатор.
    //
    // Парного `provider_cost.script_parse_byok` (разбор на ключе организации)
    // здесь намеренно нет: всё семейство `provider_cost%` вырезано запросами
    // отчёта, поэтому до подписи такая строка не доходит. Подпись создала бы
    // впечатление, что доходит.
    script_parse: 'stepNames.scriptParse',
    // AI-ассистент на платформенном ключе (docs/plans/2026-09-11-assistant.md
    // §7.5). Парного `provider_cost.assistant_byok` нет по той же причине,
    // что и у разбора скрипта выше.
    assistant: 'stepNames.assistant',
    // Шаг кастомных критериев (#860): клиенту не выставляется — входит в цену
    // минуты, — но расход наш, тип плоский, и в отчёт строка доходит. Без
    // подписи она показала бы сырое «criteria». Парного
    // `provider_cost.criteria_byok` нет по той же причине, что выше.
    criteria: 'stepNames.criteria',
    // Проверка формулировки критерия на выборке (#861): тоже наш расход и тоже
    // плоский тип, поэтому строка до отчёта доходит. Отдельная подпись, а не
    // «критерии»: оператор обязан видеть, что это трата на НАСТРОЙКУ, а не
    // себестоимость анализа. Парного `provider_cost.criteria_trial_byok` нет по
    // той же причине, что у соседей выше.
    criteria_trial: 'stepNames.criteriaTrial',
    // Черновик помощника и правка по примерам (#1416): нажатие кнопки, наш
    // расход, плоский тип — строка до отчёта доходит. Парных
    // `provider_cost.criteria_draft_byok` / `criteria_revise_byok` подписи нет
    // по той же причине, что у проверки выше.
    criteria_draft: 'stepNames.criteriaDraft',
    criteria_revise: 'stepNames.criteriaRevise',
    // Эмбеддинги поискового индекса (#756): звонок, чат, пересборка. Наш
    // расход, плоский тип, клиенту не выставляется; пары `_byok` нет вовсе.
    embedding: 'stepNames.embedding',
    // Шаг «голоса клиента» (#843/#844): наш расход, клиенту не выставляется,
    // плоский тип — строка доходит до отчёта. Парного
    // `provider_cost.voice_extraction_byok` нет по той же причине, что выше.
    voice_extraction: 'stepNames.voiceExtraction',
    // Шаг «состояние сделки» (#766): так же — наш расход, плоский тип. Парного
    // `provider_cost.deal_state_byok` (ключ организации) нет по той же
    // причине, что выше.
    deal_state: 'stepNames.dealState',
    // Черновик сообщения клиенту в карточке сделки (#1675): нажатие кнопки, наш
    // расход, плоский тип. Парный `provider_cost.deal_client_message_byok`
    // (ключ организации) есть, но подписи не получает: семейство
    // `provider_cost%` вырезано запросами отчёта, до подписи такая строка не
    // доходит.
    deal_client_message: 'stepNames.dealClientMessage',
    // Черновик FAQ (#843, §8 п. 6): нажатие кнопки, наш расход, плоский тип;
    // отдельная подпись — трата на черновик, а не себестоимость записи.
    // Парный `provider_cost.voice_faq_draft_byok` (ключ организации) есть, но
    // подписи не получает: семейство `provider_cost%` вырезано запросами
    // отчёта, до подписи такая строка не доходит.
    voice_faq_draft: 'stepNames.voiceFaqDraft',
    // Фаза стадии по названиям стадий (#1206): всегда наш ключ, плоский тип,
    // пары `_byok` нет; трата на справочник стадий, а не себестоимость записи.
    stage_phase_classify: 'stepNames.stagePhaseClassify',
    // Стартовое предложение набора тем (#845, PR 4): нажатие кнопки, наш
    // расход, плоский тип; парный `provider_cost.theme_bootstrap_byok`
    // подписи не получает по той же причине, что у черновика FAQ.
    theme_bootstrap: 'stepNames.themeBootstrap',
    // Недельная сводка новых тем (#845, PR 5b): крон, наш расход, плоский тип;
    // парный `provider_cost.theme_proposals_byok` подписи не получает по той
    // же причине, что у черновика FAQ.
    theme_proposals: 'stepNames.themeProposals',
  };
  const key = keyByCostType[costType];
  return key ? t(`reports.${key}`) : costType;
}

// ============================================================================
// Sub-components
// ============================================================================

// -- Platform scope caption ---------------------------------------------------

/**
 * The usage and per-organization P&L views sum over every organization: they
 * are the owner's platform finances, not any customer's data, and do not follow
 * the organization selected in the sidebar (#1341, В4). The single-organization
 * Financial tab does not carry this caption.
 */
function PlatformScopeCaption() {
  const { t } = useTranslation('admin');

  return (
    <div
      className="flex flex-wrap items-center gap-2 text-xs text-fg-muted"
      data-testid="reports-platform-scope"
    >
      <span className="inline-flex items-center gap-1 rounded-full border border-border bg-bg-surface px-2 py-0.5 font-medium text-fg">
        <Globe className="h-3 w-3" aria-hidden="true" />
        {t('reports.platformScope')}
      </span>
      <span>{t('reports.platformScopeHint')}</span>
    </div>
  );
}

// -- Cost per Minute Breakdown ------------------------------------------------

interface CostPerMinBreakdownProps {
  steps: UsageStep[];
  totalMinutes: number;
  totalCostCents: number;
}

function CostPerMinBreakdown({
  steps,
  totalMinutes,
  totalCostCents,
}: CostPerMinBreakdownProps) {
  const { t } = useTranslation('admin');

  const rows = steps
    .filter((s) => s.totalCostCents > 0)
    .map((s) => {
      const costPerMin = totalMinutes > 0 ? s.totalCostCents / 100 / totalMinutes : 0;
      const pct = totalCostCents > 0 ? (s.totalCostCents / totalCostCents) * 100 : 0;
      return { ...s, costPerMin, pct, label: stepLabel(s.costType, t) };
    })
    .sort((a, b) => b.totalCostCents - a.totalCostCents);

  const totalCostPerMin = totalMinutes > 0 ? totalCostCents / 100 / totalMinutes : 0;

  return (
    <Card className="p-6">
      <h2 className="text-base font-medium text-fg mb-4">
        {t('reports.costPerMinBreakdown', 'Cost per Minute Breakdown')}
      </h2>

      {/* Stacked horizontal bar */}
      <div className="h-6 flex rounded overflow-hidden mb-6">
        {rows.map((row) => (
          <div
            key={row.costType}
            style={{ width: `${row.pct}%`, backgroundColor: stepColor(row.costType) }}
            title={`${row.label}: ${row.pct.toFixed(1)}%`}
            className="transition-all"
          />
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 pr-4 text-fg-muted font-medium">
                {t('reports.table.step', 'Step')}
              </th>
              <th className="text-right py-2 pr-4 text-fg-muted font-medium">
                {t('reports.table.dollarPerMin', '$/min')}
              </th>
              <th className="text-right py-2 text-fg-muted font-medium">
                {t('reports.table.percentOfTotal', '% of total')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.costType} className="border-b border-border last:border-0">
                <td className="py-2 pr-4">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: stepColor(row.costType) }}
                    />
                    <span className="text-fg">{row.label}</span>
                  </div>
                </td>
                <td className="py-2 pr-4 text-right text-fg tabular-nums">
                  {formatRateAmount(row.costPerMin)}
                </td>
                <td className="py-2 text-right text-fg tabular-nums">
                  {row.pct.toFixed(1)}%
                </td>
              </tr>
            ))}
            <tr className="border-t border-border">
              <td className="py-2 pr-4 font-semibold text-fg">
                {t('reports.table.total', 'TOTAL')}
              </td>
              <td className="py-2 pr-4 text-right font-semibold text-fg tabular-nums">
                {formatRateAmount(totalCostPerMin)}
              </td>
              <td className="py-2 text-right font-semibold text-fg">100%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// -- Token Analytics ----------------------------------------------------------

interface TokenAnalyticsProps {
  steps: UsageStep[];
  totalTokensIn: number;
  totalTokensOut: number;
  totalMinutes: number;
  totalEntries: number;
}

function TokenAnalytics({
  steps,
  totalTokensIn,
  totalTokensOut,
  totalMinutes,
  totalEntries,
}: TokenAnalyticsProps) {
  const { t } = useTranslation('admin');

  const totalTokens = totalTokensIn + totalTokensOut;
  const tokensInPerRecord = totalEntries > 0 ? totalTokensIn / totalEntries : 0;
  const tokensOutPerRecord = totalEntries > 0 ? totalTokensOut / totalEntries : 0;
  const tokensPerRecord = totalEntries > 0 ? totalTokens / totalEntries : 0;
  const tokensInPerMin = totalMinutes > 0 ? totalTokensIn / totalMinutes : 0;
  const tokensOutPerMin = totalMinutes > 0 ? totalTokensOut / totalMinutes : 0;
  const tokensPerMin = totalMinutes > 0 ? totalTokens / totalMinutes : 0;

  const llmSteps = steps.filter(
    (s) => s.entriesWithTokens > 0 && s.totalTokensIn + s.totalTokensOut > 0
  );

  return (
    <Card className="p-6">
      <h2 className="text-base font-medium text-fg mb-4">
        {t('reports.tokenAnalytics', 'Token Analytics')}
      </h2>

      {/* Summary table */}
      <div className="overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 pr-4 text-fg-muted font-medium">
                {t('reports.table.metric', 'Metric')}
              </th>
              <th className="text-right py-2 pr-4 text-fg-muted font-medium">
                {t('reports.table.perRecord', 'Per Record (avg)')}
              </th>
              <th className="text-right py-2 pr-4 text-fg-muted font-medium">
                {t('reports.table.perMinute', 'Per Minute (avg)')}
              </th>
              <th className="text-right py-2 text-fg-muted font-medium">
                {t('reports.table.total', 'Total')}
              </th>
            </tr>
          </thead>
          <tbody>
            {[
              {
                label: t('reports.table.inputTokens', 'Input tokens'),
                perRecord: tokensInPerRecord,
                perMin: tokensInPerMin,
                total: totalTokensIn,
              },
              {
                label: t('reports.table.outputTokens', 'Output tokens'),
                perRecord: tokensOutPerRecord,
                perMin: tokensOutPerMin,
                total: totalTokensOut,
              },
              {
                label: t('reports.table.totalTokens', 'Total tokens'),
                perRecord: tokensPerRecord,
                perMin: tokensPerMin,
                total: totalTokens,
              },
            ].map((row) => (
              <tr key={row.label} className="border-b border-border last:border-0">
                <td className="py-2 pr-4 text-fg">{row.label}</td>
                <td className="py-2 pr-4 text-right text-fg tabular-nums">
                  {row.perRecord.toFixed(0)}
                </td>
                <td className="py-2 pr-4 text-right text-fg tabular-nums">
                  {row.perMin.toFixed(0)}
                </td>
                <td className="py-2 text-right text-fg tabular-nums">
                  {formatNumber(row.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per-step breakdown */}
      {llmSteps.length > 0 && (
        <>
          <h3 className="text-sm font-medium text-fg-muted mb-3">
            {t('reports.perStepBreakdown', 'Per-Step Token Breakdown')}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 pr-4 text-fg-muted font-medium">
                    {t('reports.table.step', 'Step')}
                  </th>
                  <th className="text-right py-2 pr-4 text-fg-muted font-medium">
                    {t('reports.table.avgInput', 'Avg Input')}
                  </th>
                  <th className="text-right py-2 pr-4 text-fg-muted font-medium">
                    {t('reports.table.avgOutput', 'Avg Output')}
                  </th>
                  <th className="text-right py-2 pr-4 text-fg-muted font-medium">
                    {t('reports.table.totalTokens', 'Total Tokens')}
                  </th>
                  <th className="text-right py-2 text-fg-muted font-medium">
                    {t('reports.table.effectivePer1k', 'Effective $/1K tokens')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {llmSteps.map((s) => {
                  const avgIn =
                    s.entriesWithTokens > 0 ? s.totalTokensIn / s.entriesWithTokens : 0;
                  const avgOut =
                    s.entriesWithTokens > 0 ? s.totalTokensOut / s.entriesWithTokens : 0;
                  const total = s.totalTokensIn + s.totalTokensOut;
                  const per1k = total > 0 ? (s.totalCostCents / 100 / total) * 1000 : 0;

                  return (
                    <tr key={s.costType} className="border-b border-border last:border-0">
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: stepColor(s.costType) }}
                          />
                          <span className="text-fg">{stepLabel(s.costType, t)}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-right text-fg tabular-nums">
                        {avgIn.toFixed(0)}
                      </td>
                      <td className="py-2 pr-4 text-right text-fg tabular-nums">
                        {avgOut.toFixed(0)}
                      </td>
                      <td className="py-2 pr-4 text-right text-fg tabular-nums">
                        {formatNumber(total)}
                      </td>
                      <td className="py-2 text-right text-fg tabular-nums">
                        {formatRateAmount(per1k)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

// -- Sort Icon (extracted for stable reference) --------------------------------

function SortIcon({
  col,
  sortKey,
  sortDir,
}: {
  col: OrgSortKey;
  sortKey: OrgSortKey;
  sortDir: SortDir;
}) {
  if (sortKey !== col) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
  return sortDir === 'asc' ? (
    <ArrowUp className="h-3 w-3 ml-1 text-primary" />
  ) : (
    <ArrowDown className="h-3 w-3 ml-1 text-primary" />
  );
}

// -- Organizations Table -------------------------------------------------------

interface OrgFinancialTableProps {
  organizations: OrgFinancialReportRow[];
  totals: OrgFinancialReport['totals'] | null;
}

function OrgFinancialTable({ organizations, totals }: OrgFinancialTableProps) {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<OrgSortKey>('revenueCents');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = useCallback(
    (key: OrgSortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('desc');
      }
    },
    [sortKey]
  );

  const sorted = useMemo(
    () =>
      [...organizations].sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        if (typeof av === 'number' && typeof bv === 'number') {
          return sortDir === 'asc' ? av - bv : bv - av;
        }
        return sortDir === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }),
    [organizations, sortKey, sortDir]
  );

  const colHead = (label: string, col: OrgSortKey, rightAlign = false) => (
    <th
      className={cn(
        'py-2 pr-4 text-fg-muted font-medium cursor-pointer hover:text-fg transition-colors select-none',
        rightAlign ? 'text-right' : 'text-left'
      )}
      onClick={() => handleSort(col)}
    >
      <span className="inline-flex items-center">
        {label}
        <SortIcon col={col} sortKey={sortKey} sortDir={sortDir} />
      </span>
    </th>
  );

  const marginColor = (pct: number) => {
    if (pct >= 40) return 'text-success';
    if (pct >= 20) return 'text-warning';
    return 'text-danger';
  };

  return (
    <Card className="p-6">
      <h2 className="text-base font-medium text-fg mb-4">
        {t('reports.orgsTable.title', 'Organizations P&L')}
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {colHead(t('reports.orgsTable.org', 'Organization'), 'orgName')}
              {colHead(t('reports.orgsTable.records', 'Records'), 'records', true)}
              {colHead(t('reports.orgsTable.minutes', 'Minutes'), 'minutes', true)}
              {colHead(
                t('reports.orgsTable.revenue', 'Revenue ($)'),
                'revenueCents',
                true
              )}
              {colHead(
                t('reports.orgsTable.subscription', 'Subscription ($)'),
                'subscriptionCents',
                true
              )}
              {colHead(
                t('reports.orgsTable.payments', 'Payments ($)'),
                'paymentsCents',
                true
              )}
              {colHead(
                t('reports.orgsTable.providerCost', 'Provider Cost ($)'),
                'providerCostCents',
                true
              )}
              {colHead(
                t('reports.orgsTable.margin', 'Margin (%)'),
                'marginPercent',
                true
              )}
              {colHead(
                t('reports.orgsTable.balance', 'Balance ($)'),
                'balanceCents',
                true
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((org) => (
              <tr
                key={org.orgId}
                className="border-b border-border last:border-0 hover:bg-accent transition-colors cursor-pointer"
                onClick={() =>
                  void navigate({ to: '/admin', search: { orgId: org.orgId } })
                }
              >
                <td className="py-2 pr-4 text-primary font-medium">{org.orgName}</td>
                <td className="py-2 pr-4 text-right text-fg tabular-nums">
                  {formatNumber(org.records)}
                </td>
                <td className="py-2 pr-4 text-right text-fg tabular-nums">
                  {org.minutes.toFixed(1)}
                </td>
                <td className="py-2 pr-4 text-right text-fg tabular-nums">
                  {formatCostFromCents(org.revenueCents)}
                </td>
                <td className="py-2 pr-4 text-right text-fg tabular-nums">
                  {formatCostFromCents(org.subscriptionCents)}
                </td>
                <td className="py-2 pr-4 text-right text-fg tabular-nums">
                  {formatCostFromCents(org.paymentsCents)}
                </td>
                <td className="py-2 pr-4 text-right text-fg tabular-nums">
                  {formatCostFromCents(org.providerCostCents)}
                </td>
                <td
                  className={cn(
                    'py-2 pr-4 text-right tabular-nums font-medium',
                    marginColor(org.marginPercent)
                  )}
                >
                  {org.marginPercent.toFixed(1)}%
                </td>
                <td className="py-2 text-right text-fg tabular-nums">
                  {formatCostFromCents(org.balanceCents)}
                </td>
              </tr>
            ))}
            {totals && (
              <tr className="border-t-2 border-border bg-bg-surface">
                <td className="py-2 pr-4 font-semibold text-fg">
                  {t('reports.orgsTable.totals', 'Totals')}
                </td>
                <td className="py-2 pr-4 text-right font-semibold text-fg tabular-nums">
                  {formatNumber(totals.records)}
                </td>
                <td className="py-2 pr-4 text-right font-semibold text-fg tabular-nums">
                  {totals.minutes.toFixed(1)}
                </td>
                <td className="py-2 pr-4 text-right font-semibold text-fg tabular-nums">
                  {formatCostFromCents(totals.revenueCents)}
                </td>
                <td className="py-2 pr-4 text-right font-semibold text-fg tabular-nums">
                  {formatCostFromCents(totals.subscriptionCents)}
                </td>
                <td className="py-2 pr-4 text-right font-semibold text-fg tabular-nums">
                  {formatCostFromCents(totals.paymentsCents)}
                </td>
                <td className="py-2 pr-4 text-right font-semibold text-fg tabular-nums">
                  {formatCostFromCents(totals.providerCostCents)}
                </td>
                <td
                  className={cn(
                    'py-2 pr-4 text-right font-semibold tabular-nums',
                    marginColor(totals.marginPercent)
                  )}
                >
                  {totals.marginPercent.toFixed(1)}%
                </td>
                <td className="py-2" />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ============================================================================
// Main Page Component
// ============================================================================

function AdminReportsPage() {
  const { t } = useTranslation(['admin', 'common']);
  const { selectedOrgId, isLoading: roleLoading } = useRole();
  const { apiClient } = useApiClient();
  const { authenticatedFetch } = useAuthFetch();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // --- Shared state ---
  const [activeTab, setActiveTab] = useState<ReportTab>('usage');
  const [period, setPeriod] = useState<PeriodType>('month');
  const [trendMetric, setTrendMetric] = useState<'cost' | 'tokens'>('cost');

  // The platform-wide report is not any organization's: its weeks are Monday's,
  // whichever organization is selected (#864).
  const { dateRange, navigation } = usePeriodNavigation(period, undefined, {
    weekStartsOn: DEFAULT_WEEK_STARTS_ON,
  });
  const { periodStart, periodEnd } = dateRange;
  const granularity = granularityForPeriod(period);

  // ---- Usage tab queries ----

  const usageQuery = useQuery({
    queryKey: ['admin', 'reports', 'usage', selectedOrgId, periodStart, periodEnd],
    enabled: activeTab === 'usage',
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<UsageReportData> => {
      const json = await authenticatedFetch<{ success: boolean; data: UsageReportData }>(
        `${API_BASE_URL}/api/v1/admin/reports/usage?periodStart=${encodeURIComponent(periodStart)}&periodEnd=${encodeURIComponent(periodEnd)}`
      );
      return json.data;
    },
  });

  const usageTrendQuery = useQuery({
    queryKey: [
      'admin',
      'reports',
      'usage',
      'trend',
      selectedOrgId,
      periodStart,
      periodEnd,
      granularity,
    ],
    enabled: activeTab === 'usage',
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<UsageTrendPoint[]> => {
      const json = await authenticatedFetch<{
        success: boolean;
        data: { points: UsageTrendPoint[] };
      }>(
        `${API_BASE_URL}/api/v1/admin/reports/usage/trend?periodStart=${encodeURIComponent(periodStart)}&periodEnd=${encodeURIComponent(periodEnd)}&granularity=${granularity}`
      );
      return json.data.points;
    },
  });

  // ---- Financial tab queries ----

  const financialQuery = useQuery({
    queryKey: ['admin', 'reports', 'financial', selectedOrgId, periodStart, periodEnd],
    enabled: activeTab === 'financial' && !!selectedOrgId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<{
      reportData: FinancialReportData;
      monthlyTrend: MonthlyTrend[];
    }> => {
      if (!selectedOrgId) {
        return {
          reportData: { summary: null, margin: null, periodStart, periodEnd },
          monthlyTrend: [],
        };
      }

      const [summaryData, marginData] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: billingSummaryQueryKey(selectedOrgId),
          queryFn: () => apiClient.billing.getBillingSummary(),
          staleTime: 60 * 1000,
          gcTime: 10 * 60 * 1000,
        }),
        queryClient.fetchQuery({
          queryKey: billingMarginQueryKey(selectedOrgId, periodStart, periodEnd),
          queryFn: () =>
            apiClient.billing.getMarginReport(selectedOrgId, { periodStart, periodEnd }),
          staleTime: 2 * 60 * 1000,
          gcTime: 10 * 60 * 1000,
        }),
      ]);

      // Build monthly trend (last 4 months for month periods, simpler for others)
      const trendMonths = 4;
      const trendPromises: Promise<MonthlyTrend>[] = [];
      for (let i = trendMonths - 1; i >= 0; i -= 1) {
        const monthStart = new Date(
          dateRange.start.getFullYear(),
          dateRange.start.getMonth() - i,
          1
        );
        // The month's last millisecond, like the KPI period's end: `day 0` of
        // the next month is the last day at 00:00, which dropped that whole
        // day — a subscription renewing on the 31st — out of every bar.
        const monthEnd = new Date(
          new Date(
            dateRange.start.getFullYear(),
            dateRange.start.getMonth() - i + 1,
            1
          ).getTime() - 1
        );
        trendPromises.push(
          queryClient
            .fetchQuery({
              queryKey: billingMarginQueryKey(
                selectedOrgId,
                monthStart.toISOString(),
                monthEnd.toISOString()
              ),
              queryFn: () =>
                apiClient.billing.getMarginReport(selectedOrgId, {
                  periodStart: monthStart.toISOString(),
                  periodEnd: monthEnd.toISOString(),
                }),
              staleTime: 2 * 60 * 1000,
              gcTime: 10 * 60 * 1000,
            })
            .then((data) => ({
              month: monthStart.toLocaleDateString(resolveLocale(), { month: 'short' }),
              revenue: data.margin.revenueCents / 100,
              providerCost: data.margin.totalProviderCostCents / 100,
            }))
            .catch((error) => {
              logError('admin.reports.margin_trend_month_failed', {
                component: 'AdminReports',
                orgId: selectedOrgId,
                month: monthStart.toISOString(),
                error: error instanceof Error ? error.message : 'unknown',
              });
              return {
                month: monthStart.toLocaleDateString(resolveLocale(), { month: 'short' }),
                revenue: 0,
                providerCost: 0,
              };
            })
        );
      }

      const monthlyTrend = await Promise.all(trendPromises);

      return {
        reportData: {
          summary: summaryData,
          margin: marginData.margin,
          periodStart,
          periodEnd,
        },
        monthlyTrend,
      };
    },
  });

  const orgsQuery = useQuery({
    queryKey: ['admin', 'reports', 'financial', 'orgs', periodStart, periodEnd],
    enabled: activeTab === 'organizations',
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: (): Promise<OrgFinancialReport> =>
      fetchOrgFinancialReport(authenticatedFetch, { periodStart, periodEnd }),
  });

  // ---- Error handling ----

  useEffect(() => {
    const err =
      usageQuery.error ??
      financialQuery.error ??
      orgsQuery.error ??
      usageTrendQuery.error;
    if (!err) return;
    logError('admin.reports.fetch_failed', {
      component: 'AdminReportsPage',
      error: err instanceof Error ? err.message : 'unknown_error',
    });
    toast.error(
      t('admin:reports.loadingReports', 'Failed to load report data'),
      err instanceof Error ? err.message : t('common:errors.unknown', 'Unknown error')
    );
  }, [
    usageQuery.error,
    usageQuery.errorUpdatedAt,
    financialQuery.error,
    financialQuery.errorUpdatedAt,
    orgsQuery.error,
    orgsQuery.errorUpdatedAt,
    usageTrendQuery.error,
    usageTrendQuery.errorUpdatedAt,
    toast,
    t,
  ]);

  // ---- Derived values ----

  const usageData = usageQuery.data;
  const trendPoints = useMemo(() => usageTrendQuery.data ?? [], [usageTrendQuery.data]);
  const financialData = financialQuery.data?.reportData;
  const monthlyTrend = financialQuery.data?.monthlyTrend ?? [];

  // Usage KPIs
  const totalCostCents = usageData?.totalCostCents ?? 0;
  const totalMinutes = usageData?.totalMinutes ?? 0;
  const totalEntries = usageData?.totalEntries ?? 0;
  const costPerMin = totalMinutes > 0 ? totalCostCents / 100 / totalMinutes : 0;

  // Financial KPIs
  const revenueDollars = (financialData?.margin?.revenueCents ?? 0) / 100;
  const totalProviderCostDollars =
    (financialData?.margin?.totalProviderCostCents ?? 0) / 100;
  const marginPercent = financialData?.margin?.marginPercent ?? 0;
  const finMinutes = financialData?.summary?.usage.totalMinutes ?? 0;
  const finChats = financialData?.summary?.usage.totalChats ?? 0;
  const ratePerMinute = financialData?.summary?.usage.ratePerMinute ?? 0;

  // Cost breakdown for financial tab
  const costBreakdown = useMemo(
    () =>
      (financialData?.margin?.breakdown ?? [])
        .map((item, index) => {
          const costDollars = item.providerCostCents / 100;
          const colors = ['bg-primary', 'bg-accent', 'bg-warning', 'bg-fg-muted'];
          return {
            name:
              item.type === 'asr'
                ? t('admin:reports.providerNames.transcriptionAsr', 'ASR (Transcription)')
                : item.type === 'llm'
                  ? t('admin:reports.providerNames.analysisLlm', 'Analysis (LLM)')
                  : item.type,
            cost: costDollars,
            percent:
              totalProviderCostDollars > 0
                ? (costDollars / totalProviderCostDollars) * 100
                : 0,
            color: colors[index % colors.length] ?? 'bg-primary',
          };
        })
        .sort((a, b) => b.cost - a.cost),
    [financialData, totalProviderCostDollars, t]
  );

  const prevMonthRevenue = monthlyTrend[monthlyTrend.length - 2]?.revenue ?? 0;
  const currentMonthRevenue = monthlyTrend[monthlyTrend.length - 1]?.revenue ?? 0;
  const revenueChange =
    prevMonthRevenue > 0
      ? ((currentMonthRevenue - prevMonthRevenue) / prevMonthRevenue) * 100
      : 0;

  // Trend chart data for usage tab
  const usageTrendChartData: ChartDataPoint[] = useMemo(() => {
    const buckets = new Map<
      string,
      { costCents: number; tokensIn: number; tokensOut: number; minutes: number }
    >();
    for (const p of trendPoints) {
      const existing = buckets.get(p.bucket) ?? {
        costCents: 0,
        tokensIn: 0,
        tokensOut: 0,
        minutes: 0,
      };
      existing.costCents += p.totalCostCents;
      existing.tokensIn += p.totalTokensIn;
      existing.tokensOut += p.totalTokensOut;
      buckets.set(p.bucket, existing);
    }
    return Array.from(buckets.entries()).map(([bucket, vals]) => ({
      x: bucket,
      costPerMin: vals.minutes > 0 ? vals.costCents / 100 / vals.minutes : 0,
      tokensPerMin:
        vals.minutes > 0 ? (vals.tokensIn + vals.tokensOut) / vals.minutes : 0,
    }));
  }, [trendPoints]);

  const isUsageLoading = usageQuery.isLoading;
  const isRefetching =
    activeTab === 'usage'
      ? (usageQuery.isFetching && !usageQuery.isLoading) ||
        (usageTrendQuery.isFetching && !usageTrendQuery.isLoading)
      : (financialQuery.isFetching && !financialQuery.isLoading) ||
        (orgsQuery.isFetching && !orgsQuery.isLoading);
  const isTrendLoading = usageTrendQuery.isLoading;
  const isFinancialLoading = financialQuery.isLoading;
  const isOrgsLoading = orgsQuery.isLoading;

  const handleRefreshAll = () => {
    if (activeTab === 'usage') {
      void usageQuery.refetch();
      void usageTrendQuery.refetch();
    } else {
      void financialQuery.refetch();
      void orgsQuery.refetch();
    }
  };

  // ---- Guard: loading role ----
  if (roleLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-sm text-fg-muted">{t('admin:reports.loadingReports')}</p>
        </div>
      </AdminLayout>
    );
  }

  const tabs: { value: ReportTab; label: string }[] = [
    { value: 'usage', label: t('admin:reports.tabs.usage', 'Usage') },
    { value: 'financial', label: t('admin:reports.tabs.financial', 'Financial') },
    {
      value: 'organizations',
      label: t('admin:reports.tabs.organizations', 'Organizations P&L'),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-reports-page">
        {/* Tab bar */}
        <div
          className="flex gap-1 border-b border-border"
          data-testid="admin-reports-tabs"
          role="tablist"
        >
          {tabs.map((tab) => (
            <button
              key={tab.value}
              role="tab"
              aria-selected={activeTab === tab.value}
              onClick={() => setActiveTab(tab.value)}
              data-testid={`reports-tab-${tab.value}`}
              className={cn(
                CONTROL_MIN_HEIGHT.md,
                'px-4 text-sm font-medium border-b-2 -mb-px transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                activeTab === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-fg-muted hover:text-fg hover:border-border'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Shared toolbar: period selector + actions */}
        <PeriodBar
          periods={REPORT_PERIODS.map((p) => ({
            ...p,
            label: t(`admin:reports.periodSelector.${p.id}`, p.label),
          }))}
          activePeriod={period}
          onPeriodChange={(p) => setPeriod(p as PeriodType)}
          navigation={navigation}
          isLoading={isRefetching}
          actions={
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleRefreshAll}
                disabled={isUsageLoading || isFinancialLoading}
                aria-label={t('common:common.refresh', 'Refresh')}
                className={cn(
                  CONTROL_SQUARE.md,
                  'flex items-center justify-center rounded-card',
                  'text-fg-muted hover:bg-accent hover:text-fg transition-colors duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                <RefreshCw
                  className={cn(
                    'h-4 w-4',
                    (isUsageLoading || isFinancialLoading) && 'animate-spin'
                  )}
                />
              </button>
              <ExportButton
                data={
                  (activeTab === 'usage'
                    ? (usageData?.steps ?? [])
                    : costBreakdown) as Record<string, unknown>[]
                }
                filename={`${activeTab}-report-${new Date().toISOString().split('T')[0]}`}
                columns={
                  activeTab === 'usage'
                    ? {
                        costType: t('admin:reports.table.step', 'Step'),
                        totalCostCents: t('admin:reports.costLabel', 'Cost (cents)'),
                        totalMinutes: t(
                          'admin:reports.usageLabels.transcriptionMinutes',
                          'Minutes'
                        ),
                      }
                    : {
                        name: t('common:common.name', 'Name'),
                        cost: t('admin:reports.costLabel', 'Cost'),
                        percent: '%',
                      }
                }
                formats={['csv', 'json', 'clipboard']}
                variant="default"
                size="sm"
              />
            </div>
          }
        />

        {/* ================================================================ */}
        {/* USAGE TAB                                                         */}
        {/* ================================================================ */}
        {activeTab === 'usage' && (
          <div className="space-y-6" role="tabpanel" data-testid="reports-usage-panel">
            <PlatformScopeCaption />

            {/* Error state */}
            {usageQuery.error && (
              <Card className="p-4 border-danger bg-danger-dim">
                <p className="text-sm text-danger-fg">
                  {t('admin:reports.fetchError', 'Failed to load data')}:{' '}
                  {usageQuery.error instanceof Error
                    ? usageQuery.error.message
                    : 'Unknown error'}
                </p>
              </Card>
            )}

            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                loading={isUsageLoading}
                title={t('admin:reports.costPerMinute', 'Cost per Minute')}
                value={formatRateAmount(costPerMin)}
                subtitle={t('admin:reports.avgAcrossRecords', 'avg across all records')}
                size="md"
                data-testid="reports-cost-per-min-card"
              />
              <KpiCard
                loading={isUsageLoading}
                title={t('admin:reports.minutesProcessed', 'Minutes Processed')}
                value={`${totalMinutes.toFixed(1)} ${t('admin:reports.unitMin', 'min')}`}
                subtitle={`${totalEntries} ${t('admin:reports.records', 'records')}`}
                size="md"
                data-testid="reports-minutes-card"
              />
              <KpiCard
                loading={isUsageLoading}
                title={t('admin:reports.recordsProcessed', 'Records Processed')}
                value={formatNumber(totalEntries)}
                size="md"
                data-testid="reports-records-card"
              />
              <KpiCard
                loading={isUsageLoading}
                title={t('admin:reports.totalProviderCost', 'Total Provider Cost')}
                value={formatCostFromCents(totalCostCents)}
                size="md"
                data-testid="reports-total-cost-card"
              />
            </div>

            {/* Cost per minute breakdown */}
            {usageData && usageData.steps.length > 0 && (
              <CostPerMinBreakdown
                steps={usageData.steps}
                totalMinutes={totalMinutes}
                totalCostCents={totalCostCents}
              />
            )}

            {/* Token analytics */}
            {usageData && usageData.steps.length > 0 && (
              <TokenAnalytics
                steps={usageData.steps}
                totalTokensIn={usageData.totalTokensIn}
                totalTokensOut={usageData.totalTokensOut}
                totalMinutes={totalMinutes}
                totalEntries={totalEntries}
              />
            )}

            {/* Trend chart */}
            <Card className="p-6" data-testid="reports-usage-trend-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-medium text-fg">
                  {t('admin:reports.trendOverTime', 'Trend Over Time')}
                </h2>
                <div className="flex items-center rounded-card border border-border bg-bg-elevated overflow-hidden">
                  {(
                    [
                      { value: 'cost', label: t('admin:reports.costPerMin', 'Cost/min') },
                      {
                        value: 'tokens',
                        label: t('admin:reports.tokensPerMin', 'Tokens/min'),
                      },
                    ] as const
                  ).map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setTrendMetric(m.value)}
                      className={cn(
                        'min-h-9 px-3 text-xs font-medium transition-colors duration-200',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                        trendMetric === m.value
                          ? 'bg-primary text-bg-void font-semibold'
                          : 'text-fg-muted hover:bg-accent hover:text-fg'
                      )}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              {isTrendLoading ? (
                <div className="flex items-center justify-center h-[280px]">
                  <p className="text-sm text-fg-muted">
                    {t('admin:reports.loadingTrendData', 'Loading trend data...')}
                  </p>
                </div>
              ) : usageTrendChartData.length === 0 ? (
                <div className="flex items-center justify-center h-[280px]">
                  <p className="text-sm text-fg-muted">
                    {t('admin:reports.noTrendDataAvailable', 'No trend data available')}
                  </p>
                </div>
              ) : (
                <InteractiveChart
                  data={usageTrendChartData}
                  series={[
                    {
                      dataKey: trendMetric === 'cost' ? 'costPerMin' : 'tokensPerMin',
                      name:
                        trendMetric === 'cost'
                          ? t('admin:reports.costPerMin', 'Cost/min')
                          : t('admin:reports.tokensPerMin', 'Tokens/min'),
                      color: '#4db8b3',
                      area: true,
                    },
                  ]}
                  type="area"
                  yLabel={
                    trendMetric === 'cost'
                      ? t('admin:reports.dollarPerMin', '$/min')
                      : t('admin:reports.tokensPerMin', 'Tokens/min')
                  }
                  enableGrid
                  enableLegend
                  height={280}
                />
              )}
            </Card>
          </div>
        )}

        {/* ================================================================ */}
        {/* FINANCIAL TAB                                                     */}
        {/* ================================================================ */}
        {activeTab === 'financial' && (
          <div
            className="space-y-6"
            role="tabpanel"
            data-testid="reports-financial-panel"
          >
            {/* KPI cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <KpiCard
                loading={isFinancialLoading}
                data-testid="reports-total-revenue-card"
                title={t('admin:reports.totalRevenue', 'Revenue')}
                value={formatDollars(revenueDollars)}
                trend={revenueChange >= 0 ? 'up' : 'down'}
                trendValue={Math.abs(revenueChange)}
                subtitle={t('admin:reports.vsLastMonth', 'vs last month')}
                size="lg"
              />
              <KpiCard
                loading={isFinancialLoading}
                data-testid="reports-provider-cost-card"
                title={t('admin:reports.providerCost', 'Provider Cost')}
                value={formatDollars(totalProviderCostDollars)}
                subtitle={`${finMinutes.toFixed(1)} ${t('admin:reports.unitMin', 'min')} + ${finChats} ${t('admin:reports.unitChats', 'chats')}`}
                size="lg"
              />
              <KpiCard
                loading={isFinancialLoading}
                data-testid="reports-margin-card"
                title={t('admin:reports.margin', 'Margin')}
                value={`${marginPercent.toFixed(1)}%`}
                subtitle={t('admin:reports.profitAmount', {
                  defaultValue: '{{amount}} profit',
                  amount: formatCost(revenueDollars - totalProviderCostDollars),
                })}
                size="lg"
              />
            </div>

            {/* Cost breakdown + usage details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cost breakdown by type */}
              <Card className="p-6" data-testid="reports-cost-breakdown-card">
                <h2 className="text-base font-medium text-fg mb-4">
                  {t('admin:reports.costBreakdownTitle', 'Cost Breakdown by Type')}
                </h2>
                <div className="space-y-4">
                  {costBreakdown.map((item) => (
                    <div key={item.name}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-fg">{item.name}</span>
                        <span className="text-sm font-medium text-fg tabular-nums">
                          {formatCost(item.cost)} ({item.percent.toFixed(1)}%)
                        </span>
                      </div>
                      <div className="h-2 bg-bg-elevated rounded-full overflow-hidden">
                        <div
                          className={`h-full ${item.color} transition-all duration-150`}
                          style={{ width: `${item.percent}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  {costBreakdown.length === 0 && !isFinancialLoading && (
                    <p className="text-sm text-fg-muted text-center py-4">
                      {t('admin:reports.noData', 'No data for this period')}
                    </p>
                  )}
                </div>
              </Card>

              {/* Usage details */}
              <Card className="p-6" data-testid="reports-usage-details-card">
                <h2 className="text-base font-medium text-fg mb-4">
                  {t('admin:reports.usageDetailsTitle', 'Usage Details')}
                </h2>
                <div className="space-y-3">
                  {[
                    {
                      label: t(
                        'admin:reports.usageLabels.transcriptionMinutes',
                        'Transcription Minutes'
                      ),
                      value: `${finMinutes.toFixed(1)} ${t('admin:reports.unitMin', 'min')}`,
                      testId: 'reports-usage-minutes',
                    },
                    {
                      label: t(
                        'admin:reports.usageLabels.chatsProcessed',
                        'Chats Processed'
                      ),
                      value: finChats.toString(),
                      testId: 'reports-usage-chats',
                    },
                    {
                      label: t(
                        'admin:reports.usageLabels.ratePerMinute',
                        'Rate per Minute'
                      ),
                      value: `${formatDollars(ratePerMinute)}/${t('admin:reports.unitMin', 'min')}`,
                      testId: 'reports-usage-rate',
                    },
                    {
                      label: t('admin:reports.usageLabels.revenue', 'Revenue'),
                      value: formatCost(revenueDollars),
                      testId: 'reports-usage-revenue',
                    },
                    {
                      label: t('admin:reports.usageLabels.providerCost', 'Provider Cost'),
                      value: formatCost(totalProviderCostDollars),
                      testId: 'reports-usage-provider-cost',
                    },
                  ].map((row) => (
                    <div
                      key={row.testId}
                      className="flex items-center justify-between py-2 border-b border-border last:border-0"
                      data-testid={row.testId}
                    >
                      <span className="text-sm text-fg-muted">{row.label}</span>
                      <span className="text-sm font-medium text-fg tabular-nums">
                        {row.value}
                      </span>
                    </div>
                  ))}
                  <div
                    className="flex items-center justify-between py-2"
                    data-testid="reports-usage-margin"
                  >
                    <span className="text-sm text-fg-muted">
                      {t('admin:reports.usageLabels.margin', 'Margin')}
                    </span>
                    <span className="text-sm font-medium text-success tabular-nums">
                      {marginPercent.toFixed(1)}% (
                      {formatCost(revenueDollars - totalProviderCostDollars)})
                    </span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Monthly trend chart */}
            <Card className="p-6" data-testid="reports-financial-chart-card">
              <h2 className="text-base font-medium text-fg mb-4">
                {t('admin:reports.costOverTime', 'Cost Over Time')}
              </h2>
              {isFinancialLoading ? (
                <div className="flex items-center justify-center h-[280px]">
                  <p className="text-sm text-fg-muted">
                    {t('admin:reports.loadingTrendData', 'Loading trend data...')}
                  </p>
                </div>
              ) : monthlyTrend.length === 0 ? (
                <div className="flex items-center justify-center h-[280px]">
                  <p className="text-sm text-fg-muted">
                    {t('admin:reports.noTrendDataAvailable', 'No trend data available')}
                  </p>
                </div>
              ) : (
                <InteractiveChart
                  data={monthlyTrend.map((item): ChartDataPoint => ({
                    x: item.month,
                    revenue: item.revenue,
                    providerCost: item.providerCost,
                  }))}
                  series={[
                    {
                      dataKey: 'revenue',
                      name: t('admin:reports.revenueLabel', 'Revenue'),
                      color: '#4db8b3',
                      area: true,
                    },
                    {
                      dataKey: 'providerCost',
                      name: t('admin:reports.providerCostLabel', 'Provider Cost'),
                      color: '#f59e0b',
                      area: true,
                    },
                  ]}
                  type="area"
                  xLabel={t('admin:reports.monthLabel', 'Month')}
                  yLabel={t('admin:reports.costLabel', 'Cost ($)')}
                  enableBrush
                  enableZoom
                  enableGrid
                  enableLegend
                  height={280}
                />
              )}
            </Card>
          </div>
        )}

        {/* ================================================================ */}
        {/* ORGANIZATIONS P&L TAB                                            */}
        {/* ================================================================ */}
        {activeTab === 'organizations' && (
          <div className="space-y-6" role="tabpanel" data-testid="reports-orgs-panel">
            <PlatformScopeCaption />

            {isOrgsLoading ? (
              <Card className="p-6">
                <TableSkeleton rows={5} />
              </Card>
            ) : orgsQuery.data?.organizations?.length ? (
              <OrgFinancialTable
                organizations={orgsQuery.data.organizations}
                totals={orgsQuery.data.totals ?? null}
              />
            ) : (
              <Card className="p-8 text-center">
                <p className="text-fg-muted text-sm">
                  {t(
                    'admin:reports.noOrgsData',
                    'No organization data for the selected period'
                  )}
                </p>
              </Card>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
